import React, { useEffect } from 'react';
import { useFormContext, Controller, useFieldArray } from 'react-hook-form';
import { labelStyles, getSelectStyles } from './styles';
import { FormInput, formatPhoneNumber } from '~/common/form/FormInput';
import Select from 'react-select';
import { FaPlus } from 'react-icons/fa6';
import { RiDeleteBinLine } from 'react-icons/ri';
import { phoneNumberTypesList } from '~/enums/user';

// Styles for inline phone inputs (no label included)
const inputStylesBase = 'p-[10px] text-[16px]/[20px] border-2 rounded-[4px] transition-colors';
const getInputBorderClass = (value: string | null | undefined): string => {
    return value && String(value).trim() !== '' ? 'border-black' : 'border-gray-300';
};

interface Props {
    multiplePhoneNumbers: boolean;
    states: { abbreviation: string; state: string }[];
}

interface PhoneSetError {
    phone?: { message?: string };
}

const PHONE_PATTERN = /^\(\d{3}\) \d{3}-\d{4}$/;
const PHONE_MESSAGE = 'Please enter a valid phone number in format (123) 456-7890';

const PersonDetails: React.FC<Props> = ({ states, multiplePhoneNumbers }) => {
    const {
        control,
        formState,
        getValues,
        setValue,
    } = useFormContext();
    const { errors } = formState;

    // Keep a live reference to dirtyFields so the validate closures below always
    // read the current edit state without being re-registered.
    const dirtyFieldsRef = React.useRef(formState.dirtyFields);
    dirtyFieldsRef.current = formState.dirtyFields;

    // Enforce the strict (XXX) XXX-XXXX format ONLY when the user has actually
    // edited this phone. Pre-existing values from Web Campaign Requests can be
    // non-standard; validating an untouched field would block re-saving the
    // whole Client Info form (HHLTB-2343). Empty is always allowed (optional).
    const validatePhone = (index: number) => (value: string) => {
        if (!value) return true;
        const phoneDirty = (
            dirtyFieldsRef.current as {
                person?: { phone_set?: { phone?: boolean }[] };
            }
        )?.person?.phone_set?.[index]?.phone;
        if (!phoneDirty) return true;
        return PHONE_PATTERN.test(value) || PHONE_MESSAGE;
    };

    const { fields, append, remove } = useFieldArray({
        name: 'person.phone_set',
        control,
    });

    const formattedStates = (states || []).map((state) => ({
        value: state?.abbreviation,
        label: state?.state,
    }));

    // Initialize state field with abbreviation if we have the full state name
    useEffect(() => {
        const personState = getValues('person.state');
        if (personState && formattedStates.length > 0) {
            const matchByAbbrev = formattedStates.find((s) => s.value === personState);
            if (!matchByAbbrev) {
                const matchByName = formattedStates.find((s) => s.label === personState);
                if (matchByName) {
                    setValue('person.state', matchByName.value);
                }
            }
        }
    }, [formattedStates, getValues, setValue]);

    // Get phone errors safely
    const getPhoneError = (index: number): string | undefined => {
        const phoneSetErrors = (errors.person as { phone_set?: PhoneSetError[] })?.phone_set;
        return phoneSetErrors?.[index]?.phone?.message;
    };

    return (
        <>
            {/* Basic contact fields - using FormInput for automatic dynamic borders */}
            <FormInput name="person.title" label="Title" />
            <FormInput name="person.first_name" label="First Name" required />
            <FormInput name="person.middle_name" label="Middle Name" />
            <FormInput name="person.last_name" label="Last Name" required />
            <FormInput name="person.suffix" label="Suffix" />
            <FormInput name="person.nicknames" label="Nickname" />
            <FormInput name="person.search_names" label="Search Names" />
            <FormInput name="person.email" label="Email" type="email" />

            {/* Phone numbers - keeping manual implementation for field array */}
            {multiplePhoneNumbers ? (
                <>
                    {fields.map((field, index) => (
                        <div className="mb-4" key={field.id}>
                            <div className="flex items-start gap-[8px]">
                                <div className="flex-1">
                                    <label className={`w-full ${labelStyles}`}>
                                        Phone Number {index + 1}
                                    </label>
                                    <Controller
                                        name={`person.phone_set.${index}.phone`}
                                        control={control}
                                        rules={{
                                            validate: validatePhone(index),
                                        }}
                                        render={({ field: phoneField }) => (
                                            <input
                                                type="tel"
                                                placeholder="(123) 456-7890"
                                                maxLength={14}
                                                onChange={(e) => phoneField.onChange(formatPhoneNumber(e.target.value))}
                                                value={phoneField.value || ''}
                                                className={`${inputStylesBase} ${getInputBorderClass(phoneField.value)} w-full`}
                                            />
                                        )}
                                    />
                                    {getPhoneError(index) && (
                                        <p className="mt-1 text-sm text-[#ff4b4b]">
                                            {getPhoneError(index)}
                                        </p>
                                    )}
                                </div>
                                <div className="w-1/4">
                                    <label className={`w-full ${labelStyles}`}>Type</label>
                                    <Controller
                                        name={`person.phone_set.${index}.type`}
                                        control={control}
                                        defaultValue="Cell"
                                        render={({ field: typeField }) => (
                                            <select
                                                className={`${inputStylesBase} ${typeField.value ? 'border-black' : 'border-gray-300'} w-full`}
                                                value={typeField.value || 'Cell'}
                                                onChange={typeField.onChange}
                                            >
                                                {phoneNumberTypesList.map((type) => (
                                                    <option key={type} value={type}>
                                                        {type}
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    />
                                </div>
                                {fields.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => remove(index)}
                                        className="mt-7 rounded p-2 text-[#F02D3A] transition-colors hover:bg-[#F02D3A] hover:text-white"
                                        title="Remove phone number"
                                    >
                                        <RiDeleteBinLine />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={() => append({ phone: '', type: 'Cell' })}
                        className="mb-4 flex items-center gap-2 rounded-full border-[2px] border-[#00AEB3] bg-[#00AEB3] px-[16px] py-[6px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-[#008B8F]"
                    >
                        <FaPlus /> Add Phone Number
                    </button>
                </>
            ) : (
                <>
                    <FormInput
                        name="person.phone_set.0.phone"
                        label="Phone Number"
                        type="tel"
                        placeholder="(123) 456-7890"
                        maxLength={14}
                        formatValue={formatPhoneNumber}
                        rules={{
                            validate: validatePhone(0),
                        }}
                    />
                    <Controller
                        name="person.phone_set.0.type"
                        control={control}
                        defaultValue="Cell"
                        render={() => <input type="hidden" />}
                    />
                </>
            )}

            {/* Address fields */}
            <FormInput name="person.address_1" label="Street Address 1" />
            <FormInput name="person.address_2" label="Street Address 2" />
            <FormInput name="person.city" label="City" />

            {/* State - using react-select for searchable dropdown */}
            <label htmlFor="cid-state" className={labelStyles}>
                State
            </label>
            <Controller
                name="person.state"
                control={control}
                defaultValue=""
                render={({ field }) => {
                    const selectedState = formattedStates.find(
                        (state) => state.value === field.value
                    );
                    return (
                        <Select
                            options={formattedStates}
                            className="w-full"
                            value={selectedState || null}
                            onChange={(option) => {
                                field.onChange(option?.value || '');
                            }}
                            onBlur={field.onBlur}
                            isClearable
                            styles={getSelectStyles(!!field.value)}
                        />
                    );
                }}
            />

            <FormInput name="person.zip_code" label="Zip Code" />
        </>
    );
};

export default PersonDetails;
